import * as THREE from 'three';
import { PlayerState } from '@shared/protocol';
import { Puppet } from '../player/Puppet';

interface Remote {
  puppet: Puppet;
  label: THREE.Sprite;
  target: { x: number; y: number; z: number; yaw: number; pitch: number };
}

/** Other players in the room: interpolated puppets with nickname labels. */
export class RemotePlayers {
  readonly group = new THREE.Group();
  private readonly players = new Map<string, Remote>();

  add(state: PlayerState): void {
    this.remove(state.id);
    const puppet = new Puppet(`/skins/${state.skin}.png`);
    puppet.group.position.set(state.x, state.y, state.z);
    puppet.group.rotation.y = state.yaw;

    const label = makeLabel(state.nick);
    label.position.set(0, 2.35, 0);
    puppet.group.add(label);

    this.group.add(puppet.group);
    this.players.set(state.id, {
      puppet,
      label,
      target: { x: state.x, y: state.y, z: state.z, yaw: state.yaw, pitch: state.pitch },
    });
  }

  remove(id: string): void {
    const remote = this.players.get(id);
    if (!remote) return;
    this.group.remove(remote.puppet.group);
    this.players.delete(id);
  }

  setTarget(id: string, x: number, y: number, z: number, yaw: number, pitch: number): void {
    const remote = this.players.get(id);
    if (remote) remote.target = { x, y, z, yaw, pitch };
  }

  update(dt: number): void {
    const blend = 1 - Math.exp(-12 * dt);
    for (const remote of this.players.values()) {
      const pos = remote.puppet.group.position;
      const before = pos.clone();
      pos.x += (remote.target.x - pos.x) * blend;
      pos.y += (remote.target.y - pos.y) * blend;
      pos.z += (remote.target.z - pos.z) * blend;

      const rotation = remote.puppet.group.rotation;
      rotation.y += shortestAngle(rotation.y, remote.target.yaw) * blend;
      remote.puppet.setHeadPitch(remote.target.pitch);

      const speed = dt > 0 ? Math.hypot(pos.x - before.x, pos.z - before.z) / dt : 0;
      const grounded = Math.abs(remote.target.y - pos.y) < 0.05;
      remote.puppet.update(speed, grounded, dt);
    }
  }
}

function shortestAngle(from: number, to: number): number {
  return THREE.MathUtils.euclideanModulo(to - from + Math.PI, Math.PI * 2) - Math.PI;
}

function makeLabel(nick: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 34px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = Math.min(240, ctx.measureText(nick).width + 28);
  ctx.fillStyle = 'rgba(20, 30, 45, 0.55)';
  ctx.beginPath();
  ctx.roundRect(128 - w / 2, 8, w, 48, 10);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(nick, 128, 34);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, depthTest: true }),
  );
  sprite.scale.set(1.6, 0.4, 1);
  return sprite;
}
